'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';

import { ApiClientError, createApiClient, type UserBet } from '@/lib/api';
import { categoryLabel, formatTimeUntil, formatUsdt, liveEstimateNetWinningsMicro } from '@/lib/format';
import { getInitData, hapticImpact } from '@/lib/tma';

const MAX_VISIBLE = 3;

export function UpcomingResolutions({ refreshKey }: { refreshKey: number }) {
  const [bets, setBets] = useState<UserBet[] | null>(null);

  useEffect(() => {
    const api = createApiClient(getInitData);
    api
      .myBets(100)
      .then((r) => setBets(r.bets.filter((b) => b.status === 'active')))
      .catch((err: unknown) => {
        const e = err as ApiClientError;
        console.error('UpcomingResolutions failed', e?.code);
      });
  }, [refreshKey]);

  // Сортируем по close_at ASC, берём топ MAX_VISIBLE.
  const upcoming = useMemo(() => {
    if (!bets) return [];
    return [...bets]
      .sort((a, b) => a.market_close_at - b.market_close_at)
      .slice(0, MAX_VISIBLE);
  }, [bets]);

  if (!bets || upcoming.length === 0) return null;

  const now = Math.floor(Date.now() / 1000);
  const closingSoonCount = bets.filter((b) => b.market_close_at - now < 24 * 3600).length;

  return (
    <div className="bg-bg-elev p-5 mb-4 border-l-[3px] border-warn">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] text-text-tertiary tracking-[0.15em]">
          ⏱ СКОРО РЕЗОЛВЯТСЯ
        </div>
        {closingSoonCount > 0 && (
          <span className="px-2 py-0.5 bg-no/20 text-no font-mono text-[10px] tracking-[0.05em]">
            {closingSoonCount} В 24Ч
          </span>
        )}
      </div>

      <div className="space-y-2">
        {upcoming.map((b) => {
          const winnings = liveEstimateNetWinningsMicro(
            b.amount_micro,
            b.side,
            b.pool_yes_at_bet_micro,
            b.pool_no_at_bet_micro,
            b.current_pool_yes_micro,
            b.current_pool_no_micro,
            b.market_fee_bps,
          );
          const sideLabel = b.side === 1 ? 'ДА' : 'НЕТ';
          const sideColor = b.side === 1 ? 'text-yes' : 'text-no';
          const isCloseSoon = b.market_close_at - now < 24 * 3600;

          return (
            <Link
              key={b.prediction_id}
              href={`/market?id=${encodeURIComponent(b.market_id)}`}
              onClick={() => hapticImpact('light')}
              className="block bg-bg-elev2 p-3 transition-colors active:scale-[0.99] border-l-[2px] border-line"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-[9px] text-text-tertiary tracking-[0.15em] mb-1">
                    {categoryLabel(b.market_category).toUpperCase()}
                  </p>
                  <p className="text-[13px] leading-snug truncate">{b.market_question}</p>
                </div>
                <span
                  className={`font-mono text-[10px] whitespace-nowrap tabular-nums tracking-[0.05em] ${
                    isCloseSoon ? 'text-no font-bold' : 'text-text-tertiary'
                  }`}
                >
                  {formatTimeUntil(b.market_close_at)}
                </span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-text-secondary">
                  {formatUsdt(b.amount_micro)} на{' '}
                  <span className={`${sideColor} font-bold`}>{sideLabel}</span>
                </span>
                {winnings > 0 && (
                  <span className="display text-yes tabular-nums">
                    +{formatUsdt(winnings)}
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
